'use client';

import Link from 'next/link';
import { ClipboardCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { dashboardToneClasses, formatDashboardNumber } from './analytics-dashboard-style';
import type { AnalyticsHubData, AnalyticsHubTone } from './analytics-dashboard-types';

interface ChecklistProgressWidgetProps {
  checklists: AnalyticsHubData['checklists'];
}

export function ChecklistProgressWidget({ checklists }: ChecklistProgressWidgetProps) {
  const tone: AnalyticsHubTone =
    checklists.missed > 0 || checklists.overdue > 2 ? 'critical' : checklists.overdue > 0 ? 'warning' : 'healthy';
  const toneClasses = dashboardToneClasses(tone);

  return (
    <Card className="h-full overflow-hidden rounded-3xl border-[var(--app-border)] bg-[var(--app-surface)] shadow-sm">
      <CardHeader className="relative z-10 shrink-0 pb-3">
        <CardTitle className="flex items-center justify-between gap-2 text-base">
          <span className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-primary-600" />
            Чек-листы
          </span>
          <Link href="/checklists/analytics" className="text-xs font-medium text-primary-700 hover:underline">
            отчёт
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div
            className={`mx-auto flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-gradient-to-br sm:mx-0 ${toneClasses.gradient} ring-8 ${toneClasses.ring}`}
          >
            <div className="text-center">
              <p className="text-3xl font-black tabular-nums text-[var(--app-text)]">{Math.round(checklists.completion_rate)}%</p>
              <p className="text-[10px] font-semibold text-[var(--app-text-muted)]">выполнено</p>
            </div>
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            <Metric label="Всего" value={checklists.total} />
            <Metric label="В работе" value={checklists.in_progress + checklists.pending} />
            <Metric label="Пропуски" value={checklists.missed} tone={checklists.missed > 0 ? 'critical' : 'healthy'} />
          </div>
        </div>
        <div className="mt-4 h-3 overflow-hidden rounded-full bg-[var(--app-surface-soft)]">
          <div className={toneClasses.bar} style={{ width: `${Math.max(4, checklists.completion_rate)}%`, height: '100%' }} />
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <SoftBox label="Просрочено" value={checklists.overdue} />
          <SoftBox label="Проблемные точки" value={checklists.problem_locations} />
        </div>
      </CardContent>
    </Card>
  );
}

function Metric({ label, value, tone = 'healthy' }: { label: string; value: number; tone?: AnalyticsHubTone }) {
  const toneClasses = dashboardToneClasses(tone);
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-[var(--app-text-soft)]">{label}</span>
      <span className={`rounded-full px-2.5 py-1 text-xs font-bold tabular-nums ${toneClasses.soft}`}>
        {formatDashboardNumber(value)}
      </span>
    </div>
  );
}

function SoftBox({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl bg-[var(--app-surface-soft)] px-3 py-2">
      <p className="text-lg font-black tabular-nums text-[var(--app-text)]">{formatDashboardNumber(value)}</p>
      <p className="text-[10px] text-[var(--app-text-muted)]">{label}</p>
    </div>
  );
}

